import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { TriangleAlert } from 'lucide-react';
import { formatCurrency, toMoneyString } from '@/lib/i18n';
import { api } from '@/lib/api';
import { usePermission } from '@/Hooks/usePermission';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog } from '@/components/ui/dialog';
import { Field, Textarea } from '@/components/ui/field';
import { formatClock } from './clock';
import {
  type CashierShift,
  type ShiftSummary,
  apiError,
  fromMinor,
  moneyStep,
  toMinor,
} from './lib';
import { useSubmissionKey } from './useSubmissionKey';

interface Props {
  branchId: string | null;
  currency: string;
  /**
   * Bumped by the page whenever the check list reloads. A payment taken on a
   * check is cash in this drawer, so the strip re-reads rather than sitting on
   * a figure the cashier has already changed.
   */
  refreshKey?: number;
  /**
   * The open drawer, reported upwards. `CheckDetail` needs it to book a cash
   * payment to a shift, and the page used to fetch `/cashier-shifts/current` a
   * second time to get it — two requests answering the same question, free to
   * disagree.
   */
  onShiftChange?: (shift: CashierShift | null) => void;
}

/**
 * §17 — the cashier's drawer, as one line.
 *
 * The drawer is a *fact about the branch*, not about whichever check is on
 * screen, so it reads as a status strip across the top of the till rather than
 * a panel competing with the day's list for a column. Every capability it had
 * as a panel is still here, one press away: the figures and the cash-movement
 * form behind **Drawer details**, the count-and-close behind **Close shift**,
 * and opening a drawer behind **Open shift**.
 *
 * `expectedCash` is derived server-side on every read, so a mis-posted payment
 * self-heals rather than leaving a stale figure.
 */
export default function ShiftStrip({ branchId, currency, refreshKey = 0, onShiftChange }: Props) {
  const { t, i18n } = useTranslation();
  const { can } = usePermission();
  const canOperate = can('payments.create');

  const [shift, setShift] = useState<CashierShift | null>(null);
  const [summary, setSummary] = useState<ShiftSummary | null>(null);
  const [loading, setLoading] = useState(false);
  // D-09 — the drawer has THREE states, not two: open, closed, and unknown.
  // `error` is the third. Nothing that offers to open a drawer may render while
  // it is set.
  const [error, setError] = useState<string | null>(null);
  const [summaryError, setSummaryError] = useState<string | null>(null);

  // Drawer figures are entered in this branch's currency, so their defaults and
  // their `step` follow its minor unit rather than an assumed two places.
  const step = moneyStep(currency);
  const [openFloat, setOpenFloat] = useState(() => toMoneyString(500, currency));
  const [movement, setMovement] = useState(() => ({
    amount: toMoneyString(0, currency),
    reason: '',
  }));
  /**
   * S-27 — §34 replay protection for the drawer. Deliberately NOT the
   * per-dialog key the payment dialogs use: this form stays mounted for
   * movement after movement, so the key follows the submission. See
   * `useSubmissionKey`.
   */
  const movementKey = useSubmissionKey('cash-movement');

  const [openOpen, setOpenOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [countedCash, setCountedCash] = useState(() => toMoneyString(0, currency));
  const [closeNotes, setCloseNotes] = useState('');

  // Branch switches and refreshes overlap, so every load carries a token and a
  // late response from a superseded one is dropped rather than applied.
  const requestId = useRef(0);

  // Held in a ref so a caller that passes an inline arrow does not re-run the
  // load on every render of the page above.
  const reportRef = useRef(onShiftChange);
  useEffect(() => {
    reportRef.current = onShiftChange;
  });

  /**
   * D-09 — the summary is its OWN request with its own failure. It used to
   * share the `/current` promise chain, so a transient 500 on the totals threw
   * into the same `catch` and made a live shift disappear from the screen.
   * Whether a drawer is open is what `/current` answers; this endpoint only
   * says how much is in it.
   */
  const loadSummary = useCallback(
    async (shiftId: string, token: number) => {
      try {
        const { data } = await api.get<ShiftSummary>(`/cashier-shifts/${shiftId}/summary`);
        if (token !== requestId.current) return;
        setSummary(data);
        setCountedCash(data.expectedCash);
      } catch (e) {
        if (token !== requestId.current) return;
        setSummary(null);
        setSummaryError(apiError(e, t('billing.shift.summaryFailed')));
      }
    },
    [t],
  );

  const load = useCallback(() => {
    const token = ++requestId.current;
    setError(null);
    setSummaryError(null);
    if (!branchId) {
      setShift(null);
      setSummary(null);
      setLoading(false);
      reportRef.current?.(null);
      return;
    }
    setLoading(true);
    api
      .get<CashierShift | null>('/cashier-shifts/current', { params: { branchId } })
      .then(async ({ data }) => {
        if (token !== requestId.current) return;
        const current = data ?? null;
        setShift(current);
        setSummary(null);
        reportRef.current?.(current);
        if (current?.id) await loadSummary(current.id, token);
      })
      .catch((e) => {
        if (token !== requestId.current) return;
        // D-09 — "no drawer is open" is a 200 with an empty body. A 403, a 500
        // or a dropped connection is not an answer at all, and treating it as
        // one put an **Open shift** button in front of a cashier whose branch
        // already had a drawer open — or let a cash payment go out with
        // `shiftId: undefined`, landing the money nowhere.
        setShift(null);
        setSummary(null);
        reportRef.current?.(null);
        setError(apiError(e, t('billing.shift.loadFailed')));
      })
      .finally(() => {
        if (token === requestId.current) setLoading(false);
      });
  }, [branchId, loadSummary, t]);

  useEffect(() => {
    // `refreshKey` is the page saying "money moved". It is a reload trigger
    // rather than an input to the request, so it is read and discarded.
    void refreshKey;
    load();
  }, [load, refreshKey]);

  const openShift = async () => {
    if (!branchId) return;
    try {
      await api.post('/cashier-shifts', {
        branchId,
        openingFloat: toMoneyString(openFloat, currency),
      });
      toast.success(t('billing.shift.opened'));
      setOpenOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e, t('billing.shift.failed')));
    }
  };

  const cashMovement = async (direction: 'cash-in' | 'cash-out') => {
    if (!shift) return;
    // Sent at this branch currency's own scale — three places in Kuwait, none
    // in Japan (S-26). The server checks it against the branch it resolves for
    // itself; this only keeps a typed "75.5" from arriving as a shorter number
    // than the till is counting in.
    const amount = toMoneyString(movement.amount, currency);
    const reason = movement.reason;
    const key = movementKey.keyFor(`${shift.id}:${direction}:${amount}:${reason.trim()}`);
    try {
      await api.post(`/cashier-shifts/${shift.id}/${direction}`, {
        amount,
        reason,
        idempotencyKey: key,
      });
      // Accepted — retire the key. The next drop is a new movement, not a
      // replay of this one, even if the cashier types the same 50.00 again.
      movementKey.rotate();
      toast.success(
        t(direction === 'cash-in' ? 'billing.shift.cashInDone' : 'billing.shift.cashOutDone'),
      );
      setMovement({ amount: toMoneyString(0, currency), reason: '' });
      load();
    } catch (e) {
      // The key is deliberately KEPT: pressing the button again after a
      // timeout must be a replay of this movement, not a second one.
      toast.error(apiError(e, t('billing.shift.failed')));
    }
  };

  const closeShift = async () => {
    if (!shift) return;
    try {
      await api.post(`/cashier-shifts/${shift.id}/close`, {
        countedCash: toMoneyString(countedCash, currency),
        notes: closeNotes || undefined,
      });
      toast.success(t('billing.shift.closed'));
      setCloseOpen(false);
      setDetailsOpen(false);
      setCloseNotes('');
      load();
    } catch (e) {
      toast.error(apiError(e, t('billing.shift.failed')));
    }
  };

  const varianceMinor = summary
    ? toMinor(countedCash, currency) - toMinor(summary.expectedCash, currency)
    : 0;

  const money = (amount: string) => formatCurrency(amount, currency, i18n.language);

  /**
   * The drawer this strip is entitled to render. A branch switch leaves the
   * previous branch's shift in state until its own request lands, and a strip
   * that reads "SAR 1,240 in drawer" about a branch nobody is looking at is
   * worse than one that reads "Loading…".
   */
  const drawer = shift && shift.branchId === branchId ? shift : null;

  return (
    <>
      <section
        aria-label={t('billing.shift.title')}
        className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-xl border bg-surface px-4 py-2.5 shadow-sm"
      >
        {!branchId ? (
          <p className="text-sm text-ink-muted">{t('billing.shift.pickBranch')}</p>
        ) : loading && !drawer ? (
          <p className="text-sm text-ink-muted">{t('common.loading')}</p>
        ) : error ? (
          // Deliberately BEFORE the `!drawer` branch: an unknown drawer must
          // never fall through to the "no drawer, open one" affordance.
          <>
            <p className="flex min-w-0 items-start gap-2 text-sm text-red-700 dark:text-red-400">
              <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                <strong className="font-semibold">{error}</strong>{' '}
                <span className="text-ink-muted">
                  {t('billing.shift.loadFailedHint')}
                </span>
              </span>
            </p>
            <Button className="ms-auto" variant="outline" onClick={load}>
              {t('billing.shift.retry')}
            </Button>
          </>
        ) : !drawer ? (
          <>
            <p className="flex items-center gap-2 text-sm">
              <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-line-strong" aria-hidden />
              <span className="text-ink-muted">{t('billing.shift.noneOpen')}</span>
            </p>
            {canOperate && (
              <Button className="ms-auto" onClick={() => setOpenOpen(true)}>
                {t('billing.shift.openShift')}
              </Button>
            )}
          </>
        ) : (
          <>
            <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
              <span className="inline-flex items-center gap-2 font-medium">
                <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-green-500" aria-hidden />
                {t('billing.shift.openSince', {
                  time: formatClock(drawer.openedAt, i18n.language),
                })}
              </span>
              <span aria-hidden className="text-ink-muted">
                ·
              </span>
              <span className="font-semibold tabular-nums">
                {money(summary?.expectedCash ?? drawer.expectedCash)}
              </span>
              <span className="text-ink-muted">{t('billing.shift.inDrawer')}</span>
              {summaryError && (
                <span className="inline-flex items-center gap-1 text-xs text-amber-800 dark:text-amber-300">
                  <TriangleAlert className="h-3.5 w-3.5 shrink-0" />
                  {t('billing.shift.summaryFailed')}
                </span>
              )}
            </p>

            <div className="ms-auto flex flex-wrap items-center gap-2">
              <Button variant="outline" onClick={() => setDetailsOpen(true)}>
                {t('billing.shift.details')}
              </Button>
              {canOperate && (
                // The close dialog counts against `summary.expectedCash`; without
                // it the button opened a dialog that rendered nothing at all.
                <Button
                  variant="secondary"
                  disabled={!summary}
                  title={summary ? undefined : t('billing.shift.summaryFailedHint')}
                  onClick={() => setCloseOpen(true)}
                >
                  {t('billing.shift.closeShift')}
                </Button>
              )}
            </div>
          </>
        )}
      </section>

      {openOpen && (
        <Dialog
          open
          onClose={() => setOpenOpen(false)}
          title={t('billing.shift.openShift')}
          description={t('billing.shift.openHint')}
          footer={
            <>
              <Button variant="outline" onClick={() => setOpenOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button onClick={openShift}>{t('billing.shift.openShift')}</Button>
            </>
          }
        >
          <Field label={t('billing.shift.openingFloat')} required>
            <Input
              type="number"
              inputMode="decimal"
              min="0"
              step={step}
              value={openFloat}
              onChange={(e) => setOpenFloat(e.target.value)}
            />
          </Field>
        </Dialog>
      )}

      {detailsOpen && drawer && (
        <Dialog
          open
          onClose={() => setDetailsOpen(false)}
          title={t('billing.shift.title')}
          description={t('billing.shift.subtitle')}
          footer={
            <>
              <Button variant="outline" onClick={() => setDetailsOpen(false)}>
                {t('common.close')}
              </Button>
              {canOperate && (
                <Button
                  variant="secondary"
                  disabled={!summary}
                  title={summary ? undefined : t('billing.shift.summaryFailedHint')}
                  onClick={() => setCloseOpen(true)}
                >
                  {t('billing.shift.closeShift')}
                </Button>
              )}
            </>
          }
        >
          <div className="space-y-4">
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              <Row label={t('billing.shift.openedAt')} value={formatClock(drawer.openedAt, i18n.language)} />
              <Row label={t('billing.shift.openingFloat')} value={money(drawer.openingFloat)} />
              <Row label={t('billing.shift.cashIn')} value={money(drawer.cashIn)} />
              <Row label={t('billing.shift.cashOut')} value={money(drawer.cashOut)} />
              <Row
                label={t('billing.shift.expectedCash')}
                value={money(summary?.expectedCash ?? drawer.expectedCash)}
              />
            </dl>

            {summaryError && (
              <div className="space-y-2 rounded-lg border border-amber-200 dark:border-amber-900 bg-amber-50 dark:bg-amber-950/50 p-3">
                <p className="flex items-start gap-2 text-sm text-amber-800 dark:text-amber-300">
                  <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>
                    <strong className="font-semibold">{summaryError}</strong>{' '}
                    {t('billing.shift.summaryFailedHint')}
                  </span>
                </p>
                <Button variant="outline" onClick={load}>
                  {t('billing.shift.retry')}
                </Button>
              </div>
            )}

            {summary && summary.byMethod.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium">{t('billing.shift.breakdown')}</p>
                <ul className="divide-y rounded-lg border text-sm">
                  {summary.byMethod.map((row) => (
                    <li key={row.method} className="flex items-center justify-between gap-2 p-3">
                      <span>
                        {t(`billing.method.${row.method}`)}
                        <span className="ms-2 text-xs text-ink-muted">
                          {t('billing.shift.paymentCount', { count: row.payments })}
                        </span>
                      </span>
                      <span className="tabular-nums">{money(row.amount)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/*
              S-01 — the drawer, line by line.

              The figures above say how much *should* be in the till; this says
              what happened to it. A cashier whose count comes up short reads
              this list, not the total: "Petty cash for supplier delivery, Demo
              Owner, 12:17" is the only record of a movement that was made on
              purpose, and the alternative — a total that moved with nothing
              behind it — is exactly the shape of an unexplained shortfall.
            */}
            {summary?.drawer && (
              <div className="space-y-2">
                <p className="text-sm font-medium">{t('billing.shift.drawerLog.title')}</p>

                {summary.drawer.movements.length === 0 ? (
                  <p className="rounded-lg border border-dashed p-3 text-sm text-ink-muted">
                    {t('billing.shift.drawerLog.none')}
                  </p>
                ) : (
                  <ul className="divide-y rounded-lg border text-sm">
                    {summary.drawer.movements.map((entry) => {
                      const isIn = entry.type === 'IN';
                      // Rendered from the same minor units the rest of Billing
                      // uses: an OUT is the negative of its magnitude, so the
                      // sign comes from `Intl` in the operator's own locale
                      // rather than a '-' glued to the front of an RTL string.
                      const signed = isIn
                        ? entry.amount
                        : fromMinor(-toMinor(entry.amount, currency), currency);
                      return (
                        <li key={entry.id} className="flex items-start justify-between gap-3 p-3">
                          <div className="min-w-0 space-y-0.5">
                            <p className="flex flex-wrap items-center gap-2">
                              <span
                                className={`rounded px-1.5 py-0.5 text-xs font-medium ${
                                  isIn ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-900'
                                }`}
                              >
                                {t(isIn ? 'billing.shift.cashIn' : 'billing.shift.cashOut')}
                              </span>
                              <span className="min-w-0 break-words">{entry.reason}</span>
                            </p>
                            <p className="text-xs text-ink-muted">
                              {t('billing.shift.drawerLog.by', {
                                name:
                                  entry.actor?.name ??
                                  t('billing.shift.drawerLog.systemActor'),
                              })}
                              {' · '}
                              {formatClock(entry.createdAt, i18n.language)}
                            </p>
                          </div>
                          <span
                            className={`shrink-0 tabular-nums ${isIn ? 'text-green-700 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}
                          >
                            {money(signed)}
                          </span>
                        </li>
                      );
                    })}
                    <li className="flex items-center justify-between gap-2 bg-[var(--muted)]/40 p-3 text-xs">
                      <span className="text-ink-muted">
                        {t('billing.shift.drawerLog.itemisedTotals')}
                      </span>
                      <span className="tabular-nums">
                        {t('billing.shift.drawerLog.inOut', {
                          in: money(summary.drawer.cashIn),
                          out: money(summary.drawer.cashOut),
                        })}
                      </span>
                    </li>
                  </ul>
                )}

                {/*
                  A shift that traded before movements were recorded has running
                  totals with no rows behind them. Saying so is the honest read;
                  showing only the itemised figure would make that drawer look
                  like it never moved.
                */}
                {(toMinor(summary.drawer.unitemisedCashIn, currency) !== 0 ||
                  toMinor(summary.drawer.unitemisedCashOut, currency) !== 0) && (
                  <p className="rounded-lg border border-amber-200 dark:border-amber-900 bg-amber-50 dark:bg-amber-950/50 p-3 text-xs text-amber-900 dark:text-amber-200">
                    {t('billing.shift.drawerLog.unitemised', {
                      in: money(summary.drawer.unitemisedCashIn),
                      out: money(summary.drawer.unitemisedCashOut),
                    })}
                  </p>
                )}
              </div>
            )}

            {canOperate && (
              <div className="space-y-3 rounded-lg border p-3">
                <div>
                  <p className="text-sm font-medium">{t('billing.shift.movement')}</p>
                  <p className="text-xs text-ink-muted">
                    {t('billing.shift.movementHint')}
                  </p>
                </div>
                <div className="grid gap-3 sm:grid-cols-[8rem_1fr]">
                  <Field label={t('billing.shift.amount')}>
                    <Input
                      type="number"
                      inputMode="decimal"
                      min="0"
                      step={step}
                      value={movement.amount}
                      onChange={(e) => setMovement({ ...movement, amount: e.target.value })}
                    />
                  </Field>
                  <Field label={t('billing.shift.reason')} hint={t('billing.shift.reasonHint')}>
                    <Input
                      value={movement.reason}
                      onChange={(e) => setMovement({ ...movement, reason: e.target.value })}
                    />
                  </Field>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    disabled={movement.reason.trim().length < 3}
                    onClick={() => cashMovement('cash-in')}
                  >
                    {t('billing.shift.cashIn')}
                  </Button>
                  <Button
                    variant="outline"
                    disabled={movement.reason.trim().length < 3}
                    onClick={() => cashMovement('cash-out')}
                  >
                    {t('billing.shift.cashOut')}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </Dialog>
      )}

      {closeOpen && summary && (
        <Dialog
          open
          onClose={() => setCloseOpen(false)}
          title={t('billing.shift.closeShift')}
          description={t('billing.shift.closeHint')}
          footer={
            <>
              <Button variant="outline" onClick={() => setCloseOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button onClick={closeShift}>{t('billing.shift.confirmClose')}</Button>
            </>
          }
        >
          <div className="space-y-4">
            <Field label={t('billing.shift.countedCash')} required>
              <Input
                type="number"
                inputMode="decimal"
                min="0"
                step={step}
                value={countedCash}
                onChange={(e) => setCountedCash(e.target.value)}
              />
            </Field>

            <dl className="space-y-2 rounded-lg border p-4 text-sm">
              <Row label={t('billing.shift.expectedCash')} value={money(summary.expectedCash)} />
              <Row label={t('billing.shift.countedCash')} value={money(countedCash)} />
              <div className="flex items-center justify-between gap-2 border-t pt-2 font-semibold">
                <dt>{t('billing.shift.variance')}</dt>
                <dd
                  className={
                    varianceMinor === 0
                      ? 'text-green-700 dark:text-green-400'
                      : varianceMinor > 0
                        ? 'text-sky-700 dark:text-sky-400'
                        : 'text-red-600 dark:text-red-400'
                  }
                >
                  {money(fromMinor(varianceMinor, currency))}
                </dd>
              </div>
              <p className="text-xs text-ink-muted">
                {varianceMinor === 0
                  ? t('billing.shift.varianceNone')
                  : varianceMinor > 0
                    ? t('billing.shift.varianceOver')
                    : t('billing.shift.varianceShort')}
              </p>
            </dl>

            <Field label={t('billing.shift.notes')} hint={t('billing.shift.notesHint')}>
              <Textarea rows={3} value={closeNotes} onChange={(e) => setCloseNotes(e.target.value)} />
            </Field>
          </div>
        </Dialog>
      )}
    </>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <dt className="text-ink-muted">{label}</dt>
      <dd className="tabular-nums">{value}</dd>
    </div>
  );
}
